'use client';

import React, { useEffect } from 'react';
import { useValidateVendorLink } from '@/hooks/useMenuWebsiteApi';
import { CustomerOrderForm } from './CustomerOrderForm';
import { HowItWorks } from './HowItWorks';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, MapPin } from "lucide-react";
import { FaFacebook, FaInstagram, FaGlobe } from 'react-icons/fa';
import Image from 'next/image';
import { clearOrderData } from '../utils/localStorage';

interface SharedOrderPageClientProps {
  code: string;
}

export function SharedOrderPageClient({ code }: SharedOrderPageClientProps) {
  // Use the API hook to validate vendor link and get vendor info
  const { data: validationResponse, isLoading, error } = useValidateVendorLink(code);

  // Clear any residual order data when component mounts (fresh page load)
  useEffect(() => {
    // Only clear on initial load, not on navigation
    const isNavigatedFrom = window.history.length > 1 && document.referrer.includes(window.location.hostname);
    if (!isNavigatedFrom) {
      clearOrderData();
    }
  }, []);
  
  const vendorLink = validationResponse?.data?.vendor_link;
  const vendorInfo = validationResponse?.data?.vendor;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !vendorLink || !vendorInfo) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message || 'This link is invalid or has expired. Please contact the vendor for a new link.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container relative mx-auto max-w-[850px] flex-1 flex-col items-center justify-center">
      {/* Hero Section - Mobile First */}
      <div className="flex bg-white rounded-b-xl">
        <div className="w-full px-4 md:px-8 py-8">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-8 items-center sm:items-start justify-start">
            {/* Logo Section */}
            {vendorInfo.logo_url && (
              <div className="relative w-20 h-20 md:w-28 md:h-28 mb-4 md:mb-0">
                <Image
                  src={vendorInfo.logo_url}
                  alt={vendorInfo.company_name || 'Vendor Logo'}
                  fill
                  className="object-fill rounded-lg"
                />
              </div>
            )}

            {/* Info Section */}
            <div className="flex-1 flex-col text-center sm:text-left">
              <h1 className="text-2xl md:text-2xl font-bold">{vendorInfo.company_name}</h1>

              {vendorInfo.description && (
                <p className="text-xs text-muted-foreground mb-3">
                  {vendorInfo.description}
                </p>
              )}

              {/* Social Icons */}
              {(vendorInfo.facebook_url || vendorInfo.instagram_url || vendorInfo.website_url) && (
                <div className="flex items-center sm:justify-start justify-center border-t pt-3 gap-4">
                  {vendorInfo.facebook_url && (
                    <a 
                      href={vendorInfo.facebook_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <FaFacebook className="w-5 h-5" />
                    </a>
                  )}
                  {vendorInfo.instagram_url && (
                    <a 
                      href={vendorInfo.instagram_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-pink-600 transition-colors"
                    >
                      <FaInstagram className="w-5 h-5" />
                    </a>
                  )}
                  {vendorInfo.website_url && (
                    <a 
                      href={vendorInfo.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-500 transition-colors"
                    >
                      <FaGlobe className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='flex bg-gradient-to-tr from-pink-100 to-green-100 mx-2 sm:mx-4 rounded-b-xl p-4 items-center'>
        <div className="flex flex-col md:flex-row items-start gap-2 md:gap-4">
          {vendorInfo.address && (
            <div className="flex items-center justify-center md:justify-start gap-2">
              <MapPin className="h-4 w-4 stroke-gray-500" />
              <span className="text-sm truncate text-muted-foreground">{vendorInfo.address}</span>
            </div>
          )}
        </div>
      </div>

      {/* How It Works Section */}
      <HowItWorks />

      {/* Main Content */}
      <div>
        <CustomerOrderForm
          vendorId={vendorLink.vendor_id}
          linkCode={vendorLink.link_code}
        />
      </div>

      {/* Footer */}
      <footer className="mt-12 mb-6 text-left">
        <div className="sm:text-6xl text-4xl font-bold text-gray-400 mb-2">
          MenuMate.in
        </div>
        <div className="text-xl text-gray-400">
          Crafted with <span className="text-pink-500">❤️</span> in Banaglore, India
        </div>
      </footer>
    </div>
  );
}